import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Form, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import AgGridComponent from '../../../sharedcomponents/ag-grid/AgGrid';
import {
  Active,
  Endpoints,
  GenericError,
  PendingActive,
  ReduxAction,
  RequiredPlatformCategory,
  contractTypePrimary,
  UserId,
  UrlPath,
  Draft,
  Verbiage,
  Suspended,
} from './../../../constants';
import store from './../../../redux/store';
import smartHttp from '../../../helpers/smartHttp';
import { Notify } from './../../../sharedcomponents/Alert/Notify';
import './manage-user-ids.scss';

const ManageUserIds = ({
  distributorDataSet,
  editData,
  editMode,
  getUsers,
  onAddEditModalClose,
  setLoading,
  isEndUser,
  endUserDistributors,
  setEndUserDistributors,
  isViewerUser,
  updateManageUserIdsChanged,
}) => {
  const [userRowData, setUserRowData] = useState([]);
  const [currentUserRowDataRedux, setCurrentUserRowDataRedux] = useState();
  const [userInitialRowData, setUserInitialRowData] = useState([]);
  const [entitlementsAndUserIdsChanged, setEntitlementsAndUserIdsChanged] =
    useState(false);
  const [initialExUsers, setInitialExUsers] = useState([]);
  const [errordetails, setErrordetails] = useState('');
  const gridRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ManageUserIdDistributors =
    store.getState()?.dbConfig.ManageUserIdDistributors;
  const TradingEntityDistributorPlatforms = ManageUserIdDistributors?.filter(
    (item) => item.tradingEntity === 1,
  ).map((item) => item.distributorId);
  const DistributorUserIDRequiredList = ManageUserIdDistributors?.filter(
    (item) => item.isRequired === 1,
  ).map((item) => item.distributorId);
  const [isTradeEntityHide, setIsTradeEntityHide] = useState(() => {
    const rows = endUserDistributors ?? [];
    return (
      rows.length === 0 ||
      rows.every(
        (row) =>
          !TradingEntityDistributorPlatforms?.includes(row.distributorId),
      )
    );
  });

  useEffect(() => {
    let storeData = store.getState();
    // Extract the userContracts array from the store data
    let storeUserContracts = storeData?.userContracts?.userContracts;
    // Check if the contractType of the first user contract is equal to contractTypePrimary
    if (
      storeUserContracts &&
      storeUserContracts[0]?.contractType === contractTypePrimary
    ) {
      navigate(UrlPath.home);
    }
  }, [useSelector((x) => x.userContracts)]); // Re-run the effect whenever the userContracts part of the state changes

  const defaultConfig = {
    rowModelType: 'clientSide',
    isExportCSV: false,
    isExportExcel: false,
    suppressDragLeaveHidesColumns: true,
    pivotPanelShow: '',
    pagination: false,
    isAutoSizeColumns: true,
    enableCharts: false,
    rowGroupPanelShow: false,
    sideBar: false,
    overlayNoRowsTemplate: isEndUser
      ? 'Currently, you are not entitled to any platforms requiring a User ID'
      : 'Currently, none of the delivery platforms selected require a User ID',
  };
  const defaultColdef = {
    sortable: true,
    resizable: true,
    menuTabs: ['generalMenuTab', 'filterMenuTab'],
  };

  const onInputsChanged = (params) => {
    let index = userRowData.findIndex(
      (x) => x.distributorId === params.data.distributorId,
    );
    if (index >= 0) {
      userRowData[index]['userId'] = params.data.userId ?? '';
      userRowData[index]['contact_Xref_Number'] =
        params.data.contact_Xref_Number ?? '';
    }

    // Check if all mandatory fields are now filled
    if (errordetails) {
      const allValid = userRowData.every((du) => {
        // Check required user id
        if (DistributorUserIDRequiredList.includes(du.distributorId)) {
          if (!du?.userId?.trim()) return false;
        }
        // Check required trading entity
        if (TradingEntityDistributorPlatforms.includes(du.distributorId)) {
          if (!du?.contact_Xref_Number?.trim()) return false;
        }
        return true;
      });

      // Only clear error if all mandatory fields are filled
      if (allValid) {
        setErrordetails('');
      }
    }

    validateUserIdChange();
    // Refresh grid cells to update styling
    gridRef?.current?.api?.refreshCells({ force: true });
  };

  const manageUserIdColumns = [
    {
      headerName: 'User Id',
      headerTooltip: 'User Id',
      field: 'userId',
      tooltipField: 'userId',
      initialFlex: 4,
      singleClickEdit: true,
      editable: true,
      cellRenderer: (params) => {
        return (
          <div className="aggrid-input-border">
            {params.data?.userId ? params.data.userId : ''}
          </div>
        );
      },
      menuTabs: ['generalMenuTab'],
      cellClass: (params) => {
        const isRequired = DistributorUserIDRequiredList.includes(
          params.data?.distributorId,
        );
        const isEmpty = !params.data?.userId || !params.data.userId.trim();
        const showError = isRequired && isEmpty && errordetails;
        return showError
          ? 'aggrid-input-wrapper cell-error-highlight'
          : 'aggrid-input-wrapper';
      },
    },
    {
      headerName: 'Trading Entity Name',
      headerTooltip: 'Trading Entity Name',
      field: 'contact_Xref_Number',
      tooltipField: 'contact_Xref_Number',
      initialFlex: 4,
      hide: isTradeEntityHide,
      singleClickEdit: true,
      editable: (params) => {
        return (
          params?.data &&
          TradingEntityDistributorPlatforms.includes(params.data.distributorId)
        );
      },
      cellRenderer: (params) => {
        const isTradingEntity =
          params?.data &&
          TradingEntityDistributorPlatforms.includes(params.data.distributorId);
        return (
          <div
            className={`aggrid-input-border ${
              !isTradingEntity ? 'aggrid-disabled-cell' : ''
            }`}
          >
            {isTradingEntity ? params.data.contact_Xref_Number : 'N/A'}
          </div>
        );
      },
      menuTabs: ['generalMenuTab'],
      cellClass: (params) => {
        const isTradingEntity =
          params?.data &&
          TradingEntityDistributorPlatforms.includes(params.data.distributorId);
        const isEmpty =
          !params.data?.contact_Xref_Number ||
          !params.data.contact_Xref_Number.trim();
        const showError = isTradingEntity && isEmpty && errordetails;
        return showError
          ? 'aggrid-input-wrapper cell-error-highlight'
          : 'aggrid-input-wrapper';
      },
    },
    {
      headerName: 'Distributor Platform',
      headerTooltip: 'Distributor Platform',
      field: 'distributorPlatform',
      tooltipField: 'distributorPlatform',
      initialFlex: 3,
    },
    {
      headerName: 'Distributor',
      headerTooltip: 'Distributor',
      field: 'distributor',
      tooltipField: 'distributor',
      initialFlex: 3,
      cellRenderer: (params) => {
        if (
          params?.data &&
          (DistributorUserIDRequiredList.includes(params.data.distributorId) ||
            TradingEntityDistributorPlatforms.includes(
              params.data.distributorId,
            ))
        ) {
          return (
            <div style={{ position: 'relative' }}>
              <span
                className="asterik"
                style={{
                  position: 'absolute',
                  left: '-12px',
                }}
              >
                *
              </span>
              <span>{params.value}</span>
            </div>
          );
        } else return params.value;
      },
    },
    {
      headerName: 'User ID Format',
      headerTooltip: 'User ID Format',
      field: 'userIdFormat',
      tooltipField: 'userIdFormat',
      initialFlex: 3,
    },
  ];
  const onManageUserGridReadyHandler = (params) => {
    gridRef.current = params;
    if (!isEndUser) {
      gridRef?.current?.api?.showLoadingOverlay();
    }
  };
  const setInitialRowData = (initialLoadData) => {
    // Create deep copy
    let updatedUserData = JSON.parse(
      JSON.stringify(
        distributorDataSet?.results?.filter((x) =>
          RequiredPlatformCategory.includes(x.platformCategories),
        ),
      ),
    );
    if (isEndUser) {
      updateUserData(endUserDistributors, updatedUserData);
    } else {
      updateUserData(initialLoadData, updatedUserData);
    }
    updatedUserData?.sort(distributorCompare);
    let initialData = updatedUserData?.map((data) => ({ ...data }));
    const filteredInitialData = filterRelevantDistributors(initialData);
    setUserInitialRowData(filteredInitialData ?? []);
  };

  const filterRelevantDistributors = (distributors) => {
    const distributorSet = new Set(
      ManageUserIdDistributors.map((dist) => `${dist.distributorId}`),
    );
    return distributors.filter((item) =>
      distributorSet.has(`${item.distributorId}`),
    );
  };

  const updateUserData = (distributors, userData) => {
    distributors.forEach((distUser) => {
      let matchedDistributorIndex = userData?.findIndex(
        (z) => z.distributorId === distUser.distributorId,
      );
      if (matchedDistributorIndex >= 0) {
        userData[matchedDistributorIndex].userId = distUser.userId;
        userData[matchedDistributorIndex].contact_Xref_Number =
          distUser.contact_Xref_Number;
      } else {
        // add missing distributor from saved data
        const distributorInfo = distributorDataSet?.results?.find(
          (d) => d.distributorId === distUser.distributorId,
        );
        userData.push({
          ...distributorInfo, // use existing distributor info as base
          ...distUser, // override with user-specific data
          distributor:
            distributorInfo?.distributor || distUser.distributor || '',
          distributorPlatform:
            distributorInfo?.distributorPlatform ||
            distUser.distributorPlatform ||
            '',
        });
      }
    });
  };

  const saveDistributor = async (postUserDistributor) => {
    try {
      setLoading(true);
      let postDistributorUserResponse = await smartHttp.post(
        Endpoints.distributorUserApi,
        postUserDistributor,
      );
      return postDistributorUserResponse;
    } catch (err) {
      Notify({
        alert: true,
        type: 'error',
        title: GenericError.somethingWentWrong,
      });
    } finally {
      setLoading(false);
    }
  };
  const updateDistributor = async (postUserDistributor) => {
    try {
      setLoading(true);
      let postDistributorUserResponse = await smartHttp.update(
        Endpoints.distributorUserApi,
        postUserDistributor,
      );
      return postDistributorUserResponse;
    } catch (err) {
      Notify({
        alert: true,
        type: 'error',
        title: GenericError.somethingWentWrong,
      });
    } finally {
      setLoading(false);
    }
  };

  const afterUpdateAction = (displayMessage) => {
    if (!isEndUser) {
      getUsers();
      onAddEditModalClose();
    } else {
      setEndUserDistributors(userRowData);
      gridRef?.current?.api?.hideOverlay();
    }
    resetInitialState();
    Notify({
      alert: true,
      type: 'success',
      title: displayMessage,
    });
  };

  const resetInitialState = () => {
    // Reset Initial state to current changes in data
    setUserInitialRowData([...userRowData]);
  };

  const saveEntitlements = (postData) => {
    setLoading(true);
    smartHttp
      .post(Endpoints.userentitlementsApi, postData)
      .then(() => {
        smartHttp.clearCache(Endpoints.userentitlementsApi);
        dispatch({
          type: ReduxAction.updateEntitlement,
          payload: {
            updatedEntitlements: [],
          },
        });
        afterUpdateAction('User Entitlements have been updated');
      })
      .catch((err) => {
        // FEATURE DISABLED (Release july-2026) - LME Governance Check (Story 9836589)
        // let errorMsg =
        //   err?.response?.data === -3
        //     ? Verbiage.lmeEntitlementsGovernanceFailed
        //     : GenericError.somethingWentWrong;
        let errorMsg = GenericError.somethingWentWrong;
        Notify({
          alert: true,
          type: 'error',
          title: errorMsg,
        });
      })
      .finally(() => setLoading(false));
  };
  const getAllRequiredEntitlement = (
    entitlementPostData,
    intialEntitlementData,
  ) => {
    let postDetailEntitlements =
      entitlementPostData?.userEntitlements?.length > 0
        ? entitlementPostData.userEntitlements
        : [];

    // guard + work on a copy so findIndex doesn't crash
    const initial = Array.isArray(intialEntitlementData)
      ? [...intialEntitlementData]
      : [];

    //remove duplicate from  initial entitlement
    postDetailEntitlements.forEach((pd) => {
      let index = initial.findIndex(
        (ud) => ud?.distributorId === pd?.distributorId,
      );
      if (index >= 0) initial.splice(index, 1);
    });
    let allEntitlements = postDetailEntitlements
      .filter(
        (x) =>
          x.status === Active ||
          x.status === PendingActive ||
          x.status === Draft,
      )
      .concat(
        intialEntitlementData?.filter(
          (x) =>
            x.status === Active ||
            x.status === PendingActive ||
            x.status === Draft,
        ),
      );
    return allEntitlements;
  };
  const validateData = (entitlementPostData, intialEntitlementData) => {
    let allEntitlements = getAllRequiredEntitlement(
      entitlementPostData,
      intialEntitlementData,
    );
    let requiredEntitlements = allEntitlements?.filter(
      (en) =>
        DistributorUserIDRequiredList?.includes(en?.distributorId) ||
        TradingEntityDistributorPlatforms?.includes(en?.distributorId),
    );
    requiredEntitlements.map((re) => {
      let userDistributorData = userRowData.find(
        (ud) => ud.distributorId === re.distributorId,
      );
      if (TradingEntityDistributorPlatforms.includes(re.distributorId)) {
        re.isValidated =
          userDistributorData?.contact_Xref_Number &&
          userDistributorData?.contact_Xref_Number?.trim();
        if (!re.isValidated) return re;
      }
      if (DistributorUserIDRequiredList.includes(re.distributorId)) {
        re.isValidated =
          userDistributorData?.userId && userDistributorData?.userId?.trim();
      }
    });
    return requiredEntitlements;
  };
  const getChangedDistributorUsers = () => {
    let updatedDistributorUsers = [];
    let flag = false;
    userRowData.map((du) => {
      if (flag) return;
      // flag for required trading entiry
      if (!du?.contact_Xref_Number?.trim()) {
        if (TradingEntityDistributorPlatforms.includes(du.distributorId)) {
          flag = true;
          return setErrordetails(
            'Please ensure all fields indicated as mandatory are specified',
          );
        }
      }
      // flag for required user id
      if (!du?.userId?.trim()) {
        if (DistributorUserIDRequiredList.includes(du.distributorId)) {
          flag = true;
          return setErrordetails(
            'Please ensure all fields indicated as mandatory are specified',
          );
        }
      }

      let existingDistributorUsers = initialExUsers.filter(
        (x) => x.distributorId === du.distributorId,
      );
      if (existingDistributorUsers?.length > 0) {
        if (
          du.userId !== existingDistributorUsers[0].userId ||
          du.contact_Xref_Number !==
            existingDistributorUsers[0].contact_Xref_Number
        )
          updatedDistributorUsers.push(du);
      } else {
        if (du.userId || du.contact_Xref_Number)
          updatedDistributorUsers.push(du);
      }
    });
    return updatedDistributorUsers;
  };
  const saveOrUpdateDistributorUsers = async (postData) => {
    let postDataDistributorId = [];
    getChangedDistributorUsers().forEach((ur) => {
      postDataDistributorId.push({
        userId: ur.userId,
        contactId: editData?.contactId,
        distributorId: ur.distributorId,
        contact_xref_number: ur.contact_Xref_Number,
      });
    });

    let postUserDistributor = {
      distributorUsers: postDataDistributorId,
      userId: UserId,
    };
    if (postUserDistributor.distributorUsers?.length > 0) {
      let distributorSaveResponse =
        initialExUsers?.length > 0
          ? await updateDistributor(postUserDistributor)
          : await saveDistributor(postUserDistributor);
      return distributorSaveResponse;
    } else if (postData?.userEntitlements?.length > 0) return true;
  };

  const updateEntitlementsWithDistributor = async () => {
    let entitlementDetails = store.getState()?.entitlementDetails;
    let postData = entitlementDetails?.entitlementsToUpdate;
    let initialEntitlementData = entitlementDetails?.initialEntitlements;

    // seed from products if packages payload is empty (validation applies)
    if (
      (!postData || !postData.userEntitlements?.length) &&
      entitlementDetails?.productsEntitlementsToUpdate?.length
    ) {
      postData = {
        userEntitlements: [...entitlementDetails.productsEntitlementsToUpdate],
        userId: UserId,
      };
    }
    if (postData || initialEntitlementData) {
      //validate required distributor
      let requiredEntitlements = validateData(postData, initialEntitlementData);
      if (requiredEntitlements.every((x) => x.isValidated)) {
        setErrordetails('');
        //call save or update dist user
        let distributorSaveResponse =
          await saveOrUpdateDistributorUsers(postData);

        // save entitlements
        if (
          postData?.userEntitlements?.length > 0 ||
          entitlementDetails?.productsEntitlementsToUpdate?.length > 0
        ) {
          if (distributorSaveResponse) {
            if (entitlementDetails?.productsEntitlementsToUpdate?.length > 0) {
              if (!postData)
                postData = { userEntitlements: [], userId: UserId };
              entitlementDetails.productsEntitlementsToUpdate.forEach((p) => {
                // prevent dupes on final save
                if (
                  !postData.userEntitlements.some(
                    (e) => e.productcomponentid === p.productcomponentid,
                  )
                ) {
                  postData.userEntitlements.push(p);
                }
              });
            }
            saveEntitlements(postData);
          }
        } else {
          afterUpdateAction('User ID details updated successfully');
        }
      } else {
        setErrordetails(
          'Please ensure all fields indicated as mandatory are specified',
        );
      }
    } else {
      if (endUserDistributors) {
        let requiredEntitlements =
          validateEndUserDistributors(endUserDistributors);
        if (requiredEntitlements.every((x) => x.isValidated)) {
          setErrordetails('');
          let updateUserResponse = await saveOrUpdateDistributorUsers();
          if (updateUserResponse) {
            afterUpdateAction('User ID details updated successfully');
          }
        } else {
          setErrordetails(
            'Please ensure all fields indicated as mandatory are specified',
          );
        }
      } else {
        //call save or update dist user
        let updateUserResponse = await saveOrUpdateDistributorUsers();
        if (updateUserResponse) {
          afterUpdateAction('User ID details updated successfully');
        }
      }
    }
  };

  const validateEndUserDistributors = (endUserDistributors) => {
    let requiredEntitlements = endUserDistributors.filter(
      (en) =>
        (DistributorUserIDRequiredList.includes(en.distributorId) ||
          TradingEntityDistributorPlatforms.includes(en.distributorId)) &&
        distributorDataSet?.results?.some(
          (x) => x.distributorId == en.distributorId,
        ),
    );
    requiredEntitlements.map((re) => {
      let userDistributorData = userRowData.find(
        (ud) => ud.distributorId === re.distributorId,
      );
      if (TradingEntityDistributorPlatforms.includes(re.distributorId)) {
        re.isValidated =
          userDistributorData?.contact_Xref_Number &&
          userDistributorData?.contact_Xref_Number?.trim();
        if (!re.isValidated) return re;
      }
      if (DistributorUserIDRequiredList.includes(re.distributorId)) {
        re.isValidated =
          userDistributorData?.userId && userDistributorData?.userId?.trim();
      }
    });
    return requiredEntitlements;
  };

  const distributorCompare = (current, next) => {
    if (current.distributorId < next.distributorId) {
      return -1;
    }
    if (current.distributorId > next.distributorId) {
      return 1;
    }
    return 0;
  };
  const objectsEqual = (o1, o2) =>
    Object.keys(o1).length === Object.keys(o2).length &&
    o1.every((k, index) =>
      Object.keys(k).every(
        (p) => (k[p] === '' && !o2[index][p]) || k[p] === o2[index][p],
      ),
    );
  const validateButtonDisable = (entitlementPostData) => {
    let postDetailEntitlements =
      entitlementPostData?.userEntitlements?.length > 0
        ? entitlementPostData?.userEntitlements
        : [];
    let managedUsers = distributorDataSet?.results?.filter((x) =>
      RequiredPlatformCategory.includes(x.platformCategories),
    );
    let requiredEntitlements = postDetailEntitlements.filter(
      (en) =>
        managedUsers?.some((md) => md.distributorId === en.distributorId) &&
        (!en.status ||
          en.status === Active ||
          en.status === PendingActive ||
          en.status === Draft),
    );
    return requiredEntitlements?.length > 0 ? true : false;
  };

  const getFilteredEntitlementDistributors = (selectedEntitlements) => {
    const filteredEntitlements = selectedEntitlements?.filter(
      (el) => el?.contractStatus !== Suspended,
    );

    let distributorId = [];
    filteredEntitlements?.forEach((el) => {
      if (
        el?.distributorId &&
        !distributorId.some((x) => x.distributorId == el.distributorId)
      ) {
        distributorId.push({
          distributorId: el.distributorId,
          userId: el.userId,
          contact_Xref_Number: el.contact_Xref_Number,
          userIdFormat: el.userIdFormat,
        });
      }
    });
    if (distributorId.length > 0) {
      let filteredEntitlementDistributor = [];
      distributorId.forEach((idx) => {
        // try to find existing user data for this distributor
        const existingData = userInitialRowData.find(
          (obj) => obj.distributorId === idx.distributorId,
        );

        if (existingData) {
          // use existing user data
          if (isEndUser) {
            existingData.userId = idx.userId;
            existingData.contact_Xref_Number = idx.contact_Xref_Number;
          }
          if (!existingData.userIdFormat)
            existingData.userIdFormat = idx.userIdFormat;
          filteredEntitlementDistributor.push(existingData);
        } else {
          // create new entry for missing distributors
          const distributorInfo = distributorDataSet?.results?.find(
            (d) => d.distributorId === idx.distributorId,
          );
          filteredEntitlementDistributor.push({
            distributorId: idx.distributorId,
            distributor: distributorInfo?.distributor || '',
            distributorPlatform: distributorInfo?.distributorPlatform || '',
            userIdFormat: idx.userIdFormat,
            userId: idx.userId || '',
            contact_Xref_Number: idx.contact_Xref_Number || '',
          });
        }
      });
      return filteredEntitlementDistributor;
    }

    return [];
  };
  //useEffects start
  useEffect(() => {
    try {
      let entitlementDetails = store.getState()?.entitlementDetails;
      let selectedEntitlements = entitlementDetails?.selectedEntitlements;
      // Check for any selected entitlements and initial Grid Data in redux
      if (selectedEntitlements.length > 0 && userInitialRowData) {
        let distributors =
          getFilteredEntitlementDistributors(selectedEntitlements);
        const filteredDistributors = filterRelevantDistributors(distributors);
        // set for comparing, to detect changes
        if (filteredDistributors?.length > 0) {
          setCurrentUserRowDataRedux(
            // Remove object memory reference
            filteredDistributors.map((x) => {
              return { ...x };
            }),
          );
          // set grid data
          setUserRowData([...filteredDistributors]);
          gridRef?.current?.api?.hideOverlay();
        } else {
          // skip empty filtered selection to avoid clearing grid
        }
      }
    } catch (err) {}
  }, [useSelector((x) => x.entitlementDetails), userInitialRowData]);

  useEffect(() => {
    if (endUserDistributors && endUserDistributors.length > 0) {
      let distributors =
        getFilteredEntitlementDistributors(endUserDistributors);
      const filteredDistributors = filterRelevantDistributors(distributors);
      // set for comparing, to detect changes
      setCurrentUserRowDataRedux(
        // Remove object memory reference
        filteredDistributors.map((x) => {
          return { ...x };
        }),
      );
      // set grid data
      setUserRowData([...filteredDistributors]);
    }
  }, [userInitialRowData]);

  useEffect(() => {
    validateUserIdChange();
  }, [useSelector((x) => x.entitlementDetails), userRowData]);

  useEffect(() => {
    // Refresh grid cells when error state changes to update border highlighting
    if (gridRef?.current?.api) {
      gridRef.current.api.refreshCells({ force: true });
    }
  }, [errordetails]);

  useEffect(() => {
    try {
      let entitlementDetails = store.getState()?.entitlementDetails;
      let selectedEntitlements = entitlementDetails?.selectedEntitlements;

      if (selectedEntitlements) {
        if (
          selectedEntitlements.length == 0 &&
          userInitialRowData &&
          userInitialRowData.length > 0
        ) {
          setUserRowData([]);
        }

        if (selectedEntitlements.length > 0 && !entitlementsAndUserIdsChanged) {
          if (currentUserRowDataRedux) {
            setUserRowData(
              getFilteredEntitlementDistributors(selectedEntitlements),
            );
          }
        }
      }
    } catch (err) {}
  }, []);

  useEffect(() => {
    if (
      editData?.contactId &&
      editData?.contactId != '-1' &&
      distributorDataSet
    ) {
      let queryParams = {
        pageSize: 5000,
        filter: `contactId:"${editData?.contactId}"`,
      };
      smartHttp
        .get(Endpoints.distributorUserApi, queryParams)
        .then((res) => {
          if (res?.data?.results?.length > 0) {
            let managedUsers = res?.data?.results;
            managedUsers?.sort(distributorCompare);
            setInitialExUsers(managedUsers);
            setInitialRowData(managedUsers);
          } else {
            let managedUsers = distributorDataSet?.results?.filter((x) =>
              RequiredPlatformCategory.includes(x.platformCategories),
            );

            managedUsers?.sort(distributorCompare);
            let initialData = managedUsers?.map((data) => ({ ...data }));
            setUserInitialRowData(initialData ?? []);
            if (initialData.length === 0) {
              gridRef?.current?.api?.showNoRowsOverlay();
            } else {
              gridRef?.current?.api?.hideOverlay();
            }
            setUserRowData([]);
          }
        })
        .catch((err) => {
          gridRef?.current?.api?.showNoRowsOverlay();
          Notify({
            alert: true,
            type: 'error',
            title: GenericError.somethingWentWrong,
          });
        });
    }
  }, [editData, distributorDataSet]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api && userRowData.length > 0) {
      const shouldHide = userRowData.every(
        (row) => !TradingEntityDistributorPlatforms.includes(row.distributorId),
      );
      setIsTradeEntityHide(shouldHide);
      // Hide or show the column
    }
  }, [userRowData]);

  const validateUserIdChange = () => {
    try {
      let entitlementDetails = store.getState()?.entitlementDetails;
      let entitlementsPostData = entitlementDetails?.entitlementsToUpdate;
      let prdEntPostData = entitlementDetails?.updatedProductEntitlements;
      let prdEntPostDataLength = prdEntPostData?.userEntitlements?.length || 0;
      let initialRowData = !currentUserRowDataRedux
        ? []
        : currentUserRowDataRedux;
      let modifiedRowData = !userRowData ? [] : userRowData;

      if (
        ((entitlementsPostData?.userEntitlements?.length > 0 ||
          prdEntPostDataLength) &&
          validateButtonDisable(entitlementsPostData)) ||
        !objectsEqual(modifiedRowData, initialRowData)
      ) {
        setEntitlementsAndUserIdsChanged(true);
        updateManageUserIdsChanged(true);
      } else {
        setEntitlementsAndUserIdsChanged(false);
        updateManageUserIdsChanged(false);
      }
    } catch (err) {}
  };

  const textLength = 9999;
  const unSelectInputText = (params, colId) => {
    if (params && colId && params.column.getColId() === colId) {
      if (params.column && params.rowIndex != null) {
        const cellElement = document.activeElement;
        if (cellElement && typeof cellElement.blur === 'function') {
          // Call the function to unselect all text
          cellElement.setSelectionRange(textLength, textLength);
        }
      }
    }
  };

  return (
    <>
      <div className="container-fluid pt-2-5">
        <Row>
          <Col className="mb-1">
            {userRowData?.length > 0 ? (
              <span className="email-preference-note-label">
                Please add User IDs against channel partners below
              </span>
            ) : (
              <div style={{ height: '1.1rem' }}></div>
            )}
          </Col>
        </Row>
        <Row className="grid-tab-row">
          <div className="ag-grid-modal-content ag-grid-modal-content-full-height">
            <AgGridComponent
              config={defaultConfig}
              defaultColumnDef={defaultColdef}
              columns={manageUserIdColumns}
              data={userRowData}
              onCellValueChanged={onInputsChanged}
              onGridReady={onManageUserGridReadyHandler}
              onCellEditingStarted={(e) => unSelectInputText(e, 'userId')}
            />
          </div>
        </Row>

        <Row className="modal-row">
          <div className="col-8 ">
            <span className="d-block email-preference-note-label">
              <span className="text-danger">*</span> Mandatory
            </span>
            <span className="text-danger">{errordetails}</span>
          </div>
          <div className="col-4">
            <Form>
              <Form.Group className="user-save-btn-body">
                <button
                  type="button"
                  disabled={!entitlementsAndUserIdsChanged || isViewerUser}
                  className="user-save-btn btn btn-dark float-end btn-opacity"
                  onClick={updateEntitlementsWithDistributor}
                >
                  {editMode ? 'Update' : 'Save'}
                </button>
              </Form.Group>
            </Form>
          </div>
        </Row>
      </div>
    </>
  );
};

export default ManageUserIds;
